import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Form, InputNumber, Input, message, Space,
  Divider, Select, Modal, Typography, Alert, Tag, Tabs,
  Row, Col, Statistic, Switch
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { usePermissions } from '../../hooks/usePermissions';
import api from '../../services/api';

const { Option } = Select;
const { Title, Text } = Typography;

const AdminLimits = () => {
  const { canWrite, isObserver } = usePermissions();

  // 觀察者樣式
  const observerStyles = `
    .observer-readonly {
      background-color: #fafafa !important;
      cursor: not-allowed;
    }
    .observer-readonly:hover {
      background-color: #f0f0f0 !important;
    }
    .observer-readonly .ant-btn {
      pointer-events: none;
      opacity: 0.6;
    }
  `;

  React.useEffect(() => {
    if (isObserver()) {
      const style = document.createElement('style');
      style.textContent = observerStyles;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, [isObserver]);
  const [limits, setLimits] = useState([]);
  const [userLimits, setUserLimits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [form] = Form.useForm();
  const [userLimitForm] = Form.useForm();
  const [createModal, setCreateModal] = useState(false);
  const [userLimitModal, setUserLimitModal] = useState(false);
  const [limitsStats, setLimitsStats] = useState(null);

  useEffect(() => {
    fetchLimits();
    fetchUserLimits();
    fetchLimitsStats();
  }, []);

  const fetchLimits = async () => {
    setLoading(true);
    try {
      const response = await api.get('/limits');
      setLimits(response.limits || []);
    } catch (error) {
      console.error('取得限額資料失敗:', error);
      message.error('取得限額資料失敗');
    }
    setLoading(false);
  };

  const fetchUserLimits = async () => {
    try {
      const response = await api.get('/limits/user-limits');
      const userLimitsList = response.userLimits || response.data || [];
      setUserLimits(userLimitsList);
    } catch (error) {
      console.error('取得用戶限額失敗:', error);
      message.error('取得用戶限額失敗');
    }
  };

  const fetchLimitsStats = async () => {
    try {
      const response = await api.get('/limits/stats');
      setLimitsStats(response);
    } catch (error) {
      console.error('取得限額統計失敗:', error);
    }
  };

  const updateLimits = async (record, values) => {
    try {
      await api.put(`/limits/${record.id}`, values);
      message.success('限額更新成功');
      fetchLimits();
      setEditingKey('');
    } catch (error) {
      console.error('限額更新失敗:', error);
      message.error('限額更新失敗');
    }
  };

  const createLimit = async (values) => {
    try {
      await api.post('/limits', values);
      message.success('限額創建成功');
      fetchLimits();
      setCreateModal(false);
      form.resetFields();
    } catch (error) {
      console.error('限額創建失敗:', error);
      message.error('限額創建失敗');
    }
  };

  const deleteLimit = async (id) => {
    Modal.confirm({
      title: '確認刪除',
      content: '確定要刪除這個限額設定嗎？',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/limits/${id}`);
          message.success('限額刪除成功');
          fetchLimits();
        } catch (error) {
          console.error('限額刪除失敗:', error);
          message.error('限額刪除失敗');
        }
      }
    });
  };

  const setUserLimit = async (values) => {
    try {
      await api.post('/limits/user-limit', values);
      message.success('用戶限額設定成功');
      fetchUserLimits();
      setUserLimitModal(false);
      userLimitForm.resetFields();
    } catch (error) {
      console.error('用戶限額設定失敗:', error);
      message.error('用戶限額設定失敗');
    }
  };

  const removeUserLimit = async (id) => {
    if (!id) {
      message.error('無效的用戶限額 ID');
      return;
    }

    Modal.confirm({
      title: '確認移除用戶限額',
      content: `確定要移除這個用戶限額嗎？ (ID: ${id})`,
      okText: '確認移除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await api.delete(`/limits/user-limit/${id}`);
          message.success('用戶限額移除成功');
          await fetchUserLimits();
        } catch (error) {
          console.error('用戶限額移除失敗:', error);

          let errorMessage = '用戶限額移除失敗';
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }

          message.error(`移除失敗: ${errorMessage}`);
        }
      }
    });
  };

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (record) => {
    try {
      const values = await form.validateFields();
      await updateLimits(record, values);
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const getBetTypeName = (betType) => {
    const typeNames = {
      'position': '名次投注',
      'sum_value': '冠亞和值',
      'sum_big_small': '冠亞和大小',
      'sum_odd_even': '冠亞和單雙',
      'big_small': '大小',
      'odd_even': '單雙',
      'dragon_tiger': '龍虎'
    };
    return typeNames[betType] || betType;
  };

  const columns = [
    {
      title: '投注類型',
      dataIndex: 'betType',
      key: 'betType',
      render: (betType) => (
        <Space direction="vertical" size="small">
          <Text strong>{getBetTypeName(betType)}</Text>
          <Tag color="blue" size="small">{betType}</Tag>
        </Space>
      ),
      width: 150,
    },
    {
      title: '最小投注',
      dataIndex: 'minAmount',
      key: 'minAmount',
      render: (amount, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="minAmount"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: '請輸入最小投注額' },
              { type: 'number', min: 1, message: '最小投注額必須大於 0' }
            ]}
          >
            <InputNumber
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: 120 }}
            />
          </Form.Item>
        ) : (
          <Text strong>${amount?.toLocaleString()}</Text>
        );
      },
      width: 150,
    },
    {
      title: '最大投注',
      dataIndex: 'maxAmount',
      key: 'maxAmount',
      render: (amount, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="maxAmount"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: '請輸入最大投注額' },
              { type: 'number', min: 1, message: '最大投注額必須大於 0' }
            ]}
          >
            <InputNumber
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: 120 }}
            />
          </Form.Item>
        ) : (
          <Text strong>${amount?.toLocaleString()}</Text>
        );
      },
      width: 150,
    },
    {
      title: '單期最大',
      dataIndex: 'maxPerPeriod',
      key: 'maxPerPeriod',
      render: (amount, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="maxPerPeriod"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: '請輸入單期最大額' },
              { type: 'number', min: 1, message: '單期最大額必須大於 0' }
            ]}
          >
            <InputNumber
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: 120 }}
            />
          </Form.Item>
        ) : (
          <Text strong>${amount?.toLocaleString()}</Text>
        );
      },
      width: 150,
    },
    {
      title: '最後更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => save(record)}
            >
              保存
            </Button>
            <Button size="small" onClick={cancel}>
              取消
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              disabled={editingKey !== '' || !canWrite()}
              onClick={() => edit(record)}
            >
              {canWrite() ? '編輯' : '查看'}
            </Button>
            {canWrite() && (
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => deleteLimit(record.id)}
              >
                刪除
              </Button>
            )}
          </Space>
        );
      },
      width: 150,
    },
  ];

  const userLimitColumns = [
    {
      title: '用戶',
      dataIndex: 'user',
      key: 'user',
      render: (user, record) => {
        return (
          <Space direction="vertical" size="small">
            <Text strong>{user?.displayName || record.displayName || '未知用戶'}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {user?.userId || record.userId || record.id}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '投注類型',
      dataIndex: 'betType',
      key: 'betType',
      render: (betType) => betType ? getBetTypeName(betType) : '全部類型',
    },
    {
      title: '最小投注',
      dataIndex: 'minAmount',
      key: 'minAmount',
      render: (amount) => `${amount?.toLocaleString()}`,
    },
    {
      title: '最大投注',
      dataIndex: 'maxAmount',
      key: 'maxAmount',
      render: (amount) => `${amount?.toLocaleString()}`,
    },
    {
      title: '設定者',
      dataIndex: 'setter',
      key: 'setter',
      render: (setter) => setter?.displayName || '-',
    },
    {
      title: '設定時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleString('zh-TW') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const recordId = record.id || record._id || record.key;
        
        return (
          canWrite() && (
            <Button
              danger
              size="small"
              onClick={() => removeUserLimit(recordId)}
              disabled={!recordId}
            >
              移除
            </Button>
          )
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center', 
        marginBottom: 24,
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? 12 : 0
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16,
          flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
        }}>
          <Title level={2} style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>投注限額管理</Title>
          {isObserver() && (
            <Tag icon={<EyeOutlined />} color="orange" style={{ fontSize: window.innerWidth <= 768 ? '11px' : '12px' }}>
              {window.innerWidth <= 768 ? '觀察者' : '觀察者模式 - 僅供查看'}
            </Tag>
          )}
        </div>
        {canWrite() && (
          <Space wrap={window.innerWidth <= 768}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModal(true)}
              size={window.innerWidth <= 768 ? "small" : "middle"}
            >
              {window.innerWidth <= 768 ? "新增限額" : "新增限額"}
            </Button>
            <Button
              icon={<UserOutlined />}
              onClick={() => setUserLimitModal(true)}
              size={window.innerWidth <= 768 ? "small" : "middle"}
            >
              {window.innerWidth <= 768 ? "設定用戶限額" : "設定用戶限額"}
            </Button>
          </Space>
        )}
      </div>

      {limitsStats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="限額類型"
                value={limitsStats.totalLimits}
                prefix={<SettingOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="最小投注總額"
                value={limitsStats.totalMinAmount}
                prefix={<DollarOutlined />}
                formatter={(value) => `$${value?.toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="最大投注總額"
                value={limitsStats.totalMaxAmount}
                prefix={<DollarOutlined />}
                formatter={(value) => `$${value?.toLocaleString()}`}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="用戶特殊限額"
                value={limitsStats.userLimitsCount}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Alert
        message={isObserver() ? "觀察者模式" : "限額設定說明"}
        description={isObserver() ? "您目前處於觀察者模式，只能查看限額設定，無法進行任何修改操作。" : "投注限額會立即生效於所有用戶。用戶特殊限額會覆蓋系統預設限額。"}
        type={isObserver() ? "info" : "info"}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card style={isObserver() ? {
        backgroundColor: '#f5f5f5',
        border: '1px solid #d9d9d9',
        opacity: 0.8
      } : {}}>
        <Tabs
          defaultActiveKey="system"
          type="card"
          items={[
            {
              key: 'system',
              label: '系統限額',
              children: (
                <Form form={form} component={false}>
                  <Table
                    bordered
                    dataSource={limits}
                    columns={columns}
                    rowClassName={(record) => isObserver() ? "editable-row observer-readonly" : "editable-row"}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => 
                        `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                    }}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 800 }}
                  />
                </Form>
              )
            },
            {
              key: 'user',
              label: '用戶限額',
              children: (
                <Table
                  dataSource={userLimits}
                  columns={userLimitColumns}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                  }}
                  rowClassName={(record) => isObserver() ? "observer-readonly" : ""}
                  rowKey={(record) => record.id || record._id || record.key || Math.random()}
                  scroll={{ x: 800 }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* 新增限額模態框 */}
      <Modal
        title="新增投注限額"
        open={createModal}
        onOk={() => form.submit()}
        onCancel={() => setCreateModal(false)}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={createLimit}
        >
          <Form.Item
            name="betType"
            label="投注類型"
            rules={[{ required: true, message: '請選擇投注類型' }]}
          >
            <Select placeholder="選擇投注類型">
              <Option value="position">名次投注</Option>
              <Option value="sum_value">冠亞和值</Option>
              <Option value="sum_big_small">冠亞和大小</Option>
              <Option value="sum_odd_even">冠亞和單雙</Option>
              <Option value="big_small">大小</Option>
              <Option value="odd_even">單雙</Option>
              <Option value="dragon_tiger">龍虎</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="minAmount"
            label="最小投注額"
            rules={[
              { required: true, message: '請輸入最小投注額' },
              { type: 'number', min: 1, message: '最小投注額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="請輸入最小投注額"
            />
          </Form.Item>
          
          <Form.Item
            name="maxAmount"
            label="最大投注額"
            rules={[
              { required: true, message: '請輸入最大投注額' },
              { type: 'number', min: 1, message: '最大投注額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="請輸入最大投注額"
            />
          </Form.Item>
          
          <Form.Item
            name="maxPerPeriod"
            label="單期最大額"
            rules={[
              { required: true, message: '請輸入單期最大額' },
              { type: 'number', min: 1, message: '單期最大額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="請輸入單期最大額"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 設定用戶限額模態框 */}
      <Modal
        title="設定用戶限額"
        open={userLimitModal}
        onOk={() => userLimitForm.submit()}
        onCancel={() => setUserLimitModal(false)}
        width={500}
      >
        <Form
          form={userLimitForm}
          layout="vertical"
          onFinish={setUserLimit}
        >
          <Form.Item
            name="userId"
            label="用戶ID"
            rules={[
              { required: true, message: '請輸入用戶ID' },
              { 
                pattern: /^[a-zA-Z0-9_-]+$/, 
                message: '用戶ID只能包含英文字母、數字、下劃線和連接線' 
              },
              { 
                min: 3, 
                message: '用戶ID至少需要3個字元' 
              },
              { 
                max: 50, 
                message: '用戶ID不能超過50個字元' 
              }
            ]}
            extra="支援英文字母、數字、下劃線(_)和連接線(-)"
          >
            <Input
              placeholder="例如：user123、john_doe、user-001、U4af3d8c9b8e5d6f7"
              showCount
              maxLength={50}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          
          <Form.Item
            name="betType"
            label="投注類型"
            help="留空表示適用於所有投注類型"
          >
            <Select placeholder="選擇投注類型（可選）" allowClear>
              <Option value="position">名次投注</Option>
              <Option value="sum_value">冠亞和值</Option>
              <Option value="sum_big_small">冠亞和大小</Option>
              <Option value="sum_odd_even">冠亞和單雙</Option>
              <Option value="big_small">大小</Option>
              <Option value="odd_even">單雙</Option>
              <Option value="dragon_tiger">龍虎</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="minAmount"
            label="最小投注額"
            rules={[
              { required: true, message: '請輸入最小投注額' },
              { type: 'number', min: 1, message: '最小投注額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="請輸入最小投注額"
            />
          </Form.Item>
          
          <Form.Item
            name="maxAmount"
            label="最大投注額"
            rules={[
              { required: true, message: '請輸入最大投注額' },
              { type: 'number', min: 1, message: '最大投注額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="請輸入最大投注額"
            />
          </Form.Item>
          
          <Form.Item
            name="reason"
            label="設定原因"
            rules={[{ required: true, message: '請輸入設定原因' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="請說明設定此限額的原因"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminLimits;
